/**
 * Coupon entity class
 */
import { PLATFORM_TYPES } from 'app/core/constants'
import { clone } from 'app/helpers/object'
import moment from 'moment'

/**
 * FeaturedCarouselFactory
 * returns FeaturedCarousel entity class
 *
 * @class FeaturedCarouselFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.FeaturedCarousel} class
 *
 * @ngdoc factory
 * @name mwApp.factory:FeaturedCarouselFactory
 */
/* @ngInject */
export default function FeaturedCarouselFactory ($http, $injector, Entity, Type, m2mMixin, onOffMixin, alertLiteSvc, authSvc) {
  /* === FeaturedCarousel class === */

  /**
   * FeaturedCarousel model
   *
   * @class FeaturedCarousel
   * @classdesc FeaturedCarousel model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.FeaturedCarousel} FeaturedCarousel entity
   */
  function FeaturedCarousel () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  FeaturedCarousel.prototype = Object.create(Entity.prototype)
  FeaturedCarousel.prototype.constructor = FeaturedCarousel

  /* === FeaturedCarousel instance methods & fields === */

  Object.assign(FeaturedCarousel.prototype, {

    /**
     * instance fields
     *
     * @memberof models.FeaturedCarousel#
     * @type {Object}
     */
    _fields: {
      id: Number,
      type: String,
      $platforms: [],
      platformTypes: [],
      contentItems: [],
      enabled: false,
      enabledOnModulePage: true,
      updatedBy: String,
      createdBy: String,
      publishTime: String,
      unpublishTime: String,
      created: String,
      updated: String,
      networkId: Number,
      subscriptions: ['Subscription'],
      $cacheItems: []
    },

    /**
     * url template
     *
     * @memberof models.FeaturedCarousel#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/subscriptions/carousels/featured/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.FeaturedCarousel#
     * @type {String}
     */
    _publicUrlTemplate: 'networks/{{::networkId}}/carousels/featured/{{::id}}',

    /**
     * Prepare object
     *
     * @memberof models.Show#
     * @override
     */
    _prepare: function () {
      this.$cacheItems = [...this.contentItems]
      /* Note: set default values. */
    },

    /**
     * Get underlying object
     *
     * @memberof models.Show#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      this.platformTypes = this.$platforms.filter(platform => platform.checked).map(platform => platform.name)
      this.contentItems.forEach((item, index) => {
        const cacheItem = this.$cacheItems.find(i => i.contentId === item.contentId)
        if (cacheItem) {
          item.id = cacheItem.id
        }
        delete item.selected
        delete item.$highlighted
        delete item.networkId
        item.order = index + 1
        item.contentType = this.type
      })
      this.publishTime = this.enabled ? Date.now() : null
      this.publishTime = this.publishTime ? moment.utc(moment(this.publishTime).tz(moment.defaultZone.name)).format('YYYY-MM-DDTHH:mm:ss[Z]') : null
      this.created = this.created ? moment.utc(moment(this.created).tz(moment.defaultZone.name)).format('YYYY-MM-DDTHH:mm:ss[Z]') : null
      this.updated = this.updated ? moment.utc(moment(this.updated).tz(moment.defaultZone.name)).format('YYYY-MM-DDTHH:mm:ss[Z]') : null

      return Entity.prototype._object.apply(this, arguments)
    },
    /**
     * Get platforms array with checked flags, depending on network platforms array
     *
     * @memberof models.FeaturedCarousel#
     * @return {Object[]}
     */
    _enhancePlatforms: function (def, types = []) {
      var ret = clone(PLATFORM_TYPES.filter((p) =>
        !(!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED') && p.name === 'WEB')))

      for (var i = ret.length; i--;) { // disable default checked items
        ret[i].checked = false
      }
      if (types.length) {
        ret = ret.filter((p) => types.indexOf(p.name) !== -1)
      }

      if (this._isNew() && def) {
        for (let i = ret.length; i--;) {
          if (def.indexOf(ret[i].name) !== -1) {
            ret[i].checked = true
          }
        }
      } else if (this.platformTypes && this.platformTypes.length) {
        for (let i = ret.length; i--;) {
          ret[i].checked = this.platformTypes.indexOf(ret[i].name) !== -1
        }
      }
      return ret
    },

    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.BannerBundle#
     * @param {Boolean} [enabled=this.enabled] - enable or disable Entity
     * @return {Promise} promise
     */
    _onOff: function (enabled) {
      if (typeof enabled !== 'boolean') {
        enabled = this.enabled
      }

      return this._patch(this._url, enabled ? '"ENABLE"' : '"DISABLE"')
        .catch(rejection => {
          this.enabled = !this.enabled
          return rejection
        })
    },

    /**
     * Load content for carousels according type
     *
     * @memberof models.FeaturedCarousel#
     * @return {Object[]}
     */
    _loadItems: function (params) {
      return $http.get(`/nora/api/networks/${this.networkId}/subscriptions/carousels/featured/extra`, { params }).data()
        .then((response) => response.payload)
    }

  }, onOffMixin)

  /* === FeaturedCarousel static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(FeaturedCarousel, Entity)
  FeaturedCarousel._name = 'FeaturedCarousel'
  FeaturedCarousel._publicName = 'FeaturedCarousel'
  FeaturedCarousel.init()

  // permissions
  FeaturedCarousel.access = {
    view: 'viewFeaturedCarousel',
    create: 'createFeaturedCarousel',
    edit: 'editFeaturedCarousel',
    delete: 'deleteFeaturedCarousel'
  }

  return FeaturedCarousel
}
